import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ModalDialog, useModalActions } from '../../../../../../shared/component/modal-dialog'
import { IconCheck, IconCopy, IconCourseLaunch, IconDeepLinkOpen } from '../../../../../../shared/icon/icon'
import { useViewerLaunch } from '../../../../../../shared/hooks/use-viewer-launch'
import { useCanOpenInBrowser } from '../../../../../../shared/hooks/use-viewer-available'
import { NOT_ON_THE_WEB } from '../../../../../../shared/hooks/use-course-web-builds'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useCopy } from '../../../../../../shared/clipboard/clipboard'
import { courseLink } from '../../../../../../shared/deep-link/course-link'
import '../../../../../../shared/sso-workspace/sso-workspace.scss'

// The last choice of the two link options, per browser. Storage can be
// missing or refuse (a private window, blocked site data), so every read and
// write is guarded and the dialog works the same without it.
export const DEEP_LINK_OPTIONS_KEY = 'edxr.deepLink.options'

const NO_OPTIONS = { install: false, launch: false }

const readOptions = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(DEEP_LINK_OPTIONS_KEY))
    if (saved && typeof saved === 'object') return { install: saved.install === true, launch: saved.launch === true }
  } catch (e) {}
  return NO_OPTIONS
}

const writeOptions = options => {
  try {
    window.localStorage.setItem(DEEP_LINK_OPTIONS_KEY, JSON.stringify(options))
  } catch (e) {}
}

// `webBuild`: whether the course has a web build (its host's one check,
// use-course-web-builds.js); false shows no Launch and says how to get one
// (never a dead button); undefined (not known) shows it enabled.
export const CourseDeepLink = ({ targetID, nodeID, webBuild }) => {
  const modalActions = useModalActions(targetID)

  // What the link asks the app to do when it opens it (the viewers'
  // install=auto / launch=auto). Launching waits for the course to be
  // installed, so ticking "launch" ticks "install" and holds it there.
  const [options, setOptions] = useState(readOptions)
  const install = options.install || options.launch

  const choose = next => {
    setOptions(next)
    writeOptions(next)
  }
  const handleInstall = e => choose({ install: e.target.checked, launch: options.launch })
  const handleLaunch = e => choose({ install: options.install || e.target.checked, launch: e.target.checked })

  // Shareable link (shared/deep-link/course-link.js, the one minter). It names
  // no organization: the server works out the course's.
  const url = useMemo(
    () => courseLink(nodeID, { install, launch: options.launch }),
    [nodeID, install, options.launch]
  )

  const handleOpen = () => {
    window.open(url)
    modalActions.hide()
  }

  // Anyone signed in, where a viewer is configured: the same rule as the
  // course's ⋮ menus (shared/hooks/use-viewer-available.js).
  const canOpenInBrowser = useCanOpenInBrowser()

  // Mints a one-time code for the caller's own session, server-side, and
  // sends a new tab -- opened inside this click, so no popup blocker stops
  // it -- to the URL the server returns, targeting this course
  // (shared/hooks/use-viewer-launch.js, shared with the ⋮ menus and the
  // staff sidebar's Web viewer). The player always signs in, installs or
  // updates, then launches. The button is disabled while a mint is in
  // flight; a course with no web build has none (never a dead button).
  const viewerLaunch = useViewerLaunch()
  const noWebBuild = webBuild === false

  const handleOpenInBrowser = async () => {
    if (await viewerLaunch.launch(`content/${nodeID}`)) modalActions.hide()
  }

  // Copied inside the click (shared/clipboard), so Safari allows it; the
  // check only after a real copy, otherwise the link is selected.
  const copy = useCopy()
  const field = useRef(null)
  const handleCopy = () => copy.copy(url, field)

  // The whole link is shown, wrapped, so a change of option is visible on a
  // phone too: the field grows to fit it -- when the dialog is shown (a
  // hidden field has no size to measure), when the link changes and when the
  // window resizes.
  const fit = useCallback(() => {
    const el = field.current
    if (!el || el.getClientRects().length === 0) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
  }, [])
  useLayoutEffect(() => fit(), [url, fit])
  useEffect(() => {
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [fit])

  const id = `${targetID}-deep-link`

  return (
    <ModalDialog target={targetID} header="Deep link" onShow={fit}>
      <div className="sw-workspace">
        <textarea
          id={`${id}-url`}
          className="form-control"
          value={url}
          readOnly
          ref={field}
          rows={1}
          aria-label="Deep link"
          style={{ resize: 'none', overflow: 'hidden', wordBreak: 'break-all' }}
        />
        {copy.manual && <div className="sw-hint mt-1" role="status">{copy.hint}</div>}

        <fieldset className="mt-3">
          <legend className="visually-hidden">When the app opens the link</legend>
          <div className="form-check">
            <input id={`${id}-install`} className="form-check-input" type="checkbox" checked={install} disabled={options.launch} onChange={handleInstall} />
            <label className="form-check-label" htmlFor={`${id}-install`}>
              Install or update automatically
            </label>
          </div>
          <div className="form-check">
            <input
              id={`${id}-launch`}
              className="form-check-input"
              type="checkbox"
              checked={options.launch}
              aria-describedby={`${id}-launch-hint`}
              onChange={handleLaunch}
            />
            <label className="form-check-label" htmlFor={`${id}-launch`}>
              Launch when ready
            </label>
          </div>
          <p id={`${id}-launch-hint`} className="sw-hint mt-1 mb-0">
            A course can only launch once it's installed, so launching also installs or updates it.
          </p>
        </fieldset>

        <div className="sw-dialog-actions mt-3">
          <button type="button" className="btn sw-btn" onClick={handleOpen}>
            <IconDeepLinkOpen /> Open
          </button>
          {canOpenInBrowser &&
            !noWebBuild &&
            <button
              type="button"
              className="btn sw-btn"
              disabled={viewerLaunch.launching}
              onClick={handleOpenInBrowser}>
              <IconCourseLaunch /> Launch
            </button>}
          <button type="button" className="btn sw-btn-primary" onClick={handleCopy}>
            {copy.copied ? <IconCheck /> : <IconCopy />} {copy.copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {/* Where Launch would be, for a course with nothing to launch. */}
        {canOpenInBrowser &&
          noWebBuild &&
          <p className="sw-hint mt-2 mb-0">
            {NOT_ON_THE_WEB}
          </p>}

        {viewerLaunch.failure &&
          <p className="text-danger mt-2 mb-0" role="alert">
            {viewerLaunch.failure.message}
          </p>}
        {viewerLaunch.failure && <ErrorReference error={viewerLaunch.failure.error} />}
      </div>
    </ModalDialog>
  )
}
